import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Portal } from '@headlessui/react';

import ChevronDown from '@/icons/light/chevron-down.svg';
import Facebook from '@/icons/light/facebook.svg';
import Mail from '@/icons/light/mail.svg';
import Twitter from '@/icons/light/twitter.svg';
import { Button, IconText, Link, Price, Text } from '@/atoms';
import { ButtonSize, ButtonVariant } from '@/atoms/button/types';
import { IconTextVariant } from '@/atoms/icon-text/types';
import { Alert, ModalShare, ModalView360, View360 } from '@/molecules';

import type { FunctionComponent } from 'react';
import type { TypeFlatDescriptionProps } from './types';

const ModalForm = dynamic(() => import('@/molecules/modal-form'), {
  ssr: true,
});

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

const AvailableDates = dynamic(() => import('@/organisms/available-dates'), {
  ssr: true,
});

export const FlatDescription: FunctionComponent<TypeFlatDescriptionProps> = ({
  className = '',
  price,
  title,
  description,
  has360,
  src360,
  highlightedFeatures,
  features,
  location,
  flat,
  isPeriodic = true,
  availableDates,
  otrosDatos,
}: TypeFlatDescriptionProps) => {
  const [url, setUrl] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const Map = location?.location
    ? dynamic(() => import('@/molecules/map'))
    : null;
  const showLimitedAvailabilityAlert: boolean =
    availableDates &&
    availableDates.length > 1 &&
    flat.ventaAlquiler === 'MED_EST';
  const showAvailabilityCalendar: boolean =
    availableDates &&
    availableDates.length !== 0 &&
    flat.ventaAlquiler === 'MED_EST';

  useEffect(() => {
    setUrl(document.URL);
    return () => setUrl('');
  }, []);

  return (
    <>
      <div className={`flex flex-col ${className}`}>
        <h1 className="text-headline text-headline--h3 mb-4">{title}</h1>

        <div className="mb-8 flex flex-col justify-between gap-4 md:mb-12 md:flex-row md:flex-wrap md:gap-6">
          <Price
            period={isPeriodic ? '/mes' : ''}
            price={price}
            className="order-1"
          />

          <div className="order-2 flex flex-row items-center gap-x-4 md:gap-x-6">
            {has360 && src360 && (
              <ModalView360 type="link" src={src360} flat={flat} />
            )}

            <ModalShare
              className="order-3 md:order-2"
              title="Comparte esta vivienda"
              socialMedias={[
                {
                  Icon: <Mail width={32} heigth={32} />,
                  text: 'Email',
                  href: 'mailto:?subject=Feedback&body=Message',
                },
                {
                  Icon: <Twitter width={32} heigth={32} />,
                  text: 'Twitter',
                  href: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
                },
                {
                  Icon: <Facebook width={32} heigth={32} />,
                  text: 'Facebook',
                  href: `https://www.facebook.com/sharer.php?u=${url}`,
                },
              ]}
              gtm={{ itemId: flat.codigoUnidadAlquiler }}
            />
          </div>

          {highlightedFeatures && (
            <div className="order-2 w-full md:order-3">
              <div className="grid grid-cols-2 gap-4 xs:grid-cols-3 md:w-max md:grid-cols-5 xl:grid-cols-6">
                {highlightedFeatures.map((feature, index: number) => (
                  <IconText
                    Icon={feature.Icon}
                    value={feature.value}
                    variant={IconTextVariant.MEDIUM}
                    key={index}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {showLimitedAvailabilityAlert && (
          <Alert link="#available-dates" className="mb-8" />
        )}

        {description && (
          <>
            <Text className="text-headline text-headline--h4 mb-4" as="h2">
              {description.title}
            </Text>

            <div
              className="text-body text-body--m mb-8"
              dangerouslySetInnerHTML={{ __html: description.content }}
            />
          </>
        )}

        {otrosDatos && (
          <div className="mb-6 flex flex-col">
            <p className="text-headline text-headline--h4 text-text-neutrals-900">
              Otros datos de interés
            </p>

            <div
              className="text-body text-body--m text-text-neutrals-900"
              dangerouslySetInnerHTML={{ __html: otrosDatos }}
            />
          </div>
        )}

        {has360 && src360 && (
          <View360 className="mb-12" src={src360} flat={flat} />
        )}

        {features && features.features.length > 1 && (
          <div className="mb-6 flex flex-col gap-y-4">
            <Text className="text-headline text-headline--h4" as="h2">
              {features.title}
            </Text>

            {features.features && (
              <div className="grid grid-cols-1 gap-y-4 xs:grid-cols-2 md:grid-cols-3">
                {features.features.map((f, i) => (
                  <IconText
                    Icon={dynamic(() => import(`@/icons/light/${f.icon}.svg`))}
                    variant={IconTextVariant.MEDIUM}
                    key={i}
                    title={f.title}
                    value={f.renderValue ? f.value : ''}
                    {...(i > 5 &&
                      !open && {
                        className: 'hidden',
                      })}
                  />
                ))}
              </div>
            )}

            {features.features.length > 6 && (
              <Link as="button" onClick={() => setOpen(!open)}>
                {open ? 'Ver menos' : 'Ver todas'}{' '}
                <ChevronDown
                  width={16}
                  height={16}
                  {...(open && {
                    className: 'rotate-180',
                  })}
                />
              </Link>
            )}
          </div>
        )}

        {location && (
          <div className="flex flex-col gap-y-4">
            <Text className="text-headline text-headline--h4" as="h2">
              {location.title}
            </Text>

            {location?.address}

            {Map ? <Map location={location.location} minHeight={240} /> : Map}
          </div>
        )}

        {showAvailabilityCalendar && (
          <AvailableDates availableDates={availableDates} />
        )}
      </div>

      <ModalForm isOpen={isOpenModal} closeModal={() => setIsOpenModal(false)}>
        <FormsTabs flat={flat} noTitle />
      </ModalForm>

      <Portal>
        <div className="border-t-solid fixed left-0 right-0 bottom-0 z-10 flex h-16 items-center justify-between gap-4 border-t border-t-neutrals-50 bg-neutrals-0 px-4 py-2 lg:hidden">
          <Price period={isPeriodic ? '/mes' : ''} price={price} />

          <Button
            onClick={() => setIsOpenModal(true)}
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            className="w-max"
          >
            Contactar
          </Button>
        </div>
      </Portal>
    </>
  );
};

FlatDescription.displayName = 'FlatDescription';
